import PropTypes from 'prop-types';
import React, { memo, useCallback, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { fetchGroupMessages } from '../../actions/communication';
import { resetErrorMessage } from '../../actions/internal';
import { formatDateTimeDay } from '../../utils/Formatters';
import { LoadingSpinner } from '../helpers/LoadingSpinner';
import SendingAs from './SendingAs';

const MessageRow = memo(({ message }) => {
  const recipients = (message) => {
    let result = '';
    if (message.groups.length > 0) {
      result += `Groups: ${message.groups.join(', ')}; `;
    }
    if (message.cohorts.length > 0) {
      result += `Cohorts: ${message.cohorts.join(', ')}`;
    }
    return result;
  }

  return (
    <tr className='cs-bg--light'>
      <td>{message.body}</td>
      <td>{recipients(message)}</td>
      <td>{formatDateTimeDay(message.sentAt)}</td>
    </tr>
  );
});

const GroupMessages = ({
  fetchGroupMessages,
  resetErrorMessage,
  isLoading,
  messages,
  page = 1,
  totalPages = 1,
}) => {
  const observerRef = useRef(null);
  const loaderRef = useCallback(node => {
    if (isLoading) {
      return;
    }

    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMoreMessages) { // Example condition to load more
        fetchGroupMessages({ page: page + 1 });
      }
    });

    if (node) {
      observerRef.current.observe(node);
    }
  }, [isLoading, fetchGroupMessages]);

  useEffect(() => {
    resetErrorMessage();
    if (messages.length === 0) {
      fetchGroupMessages();
    }
  }, [fetchGroupMessages, resetErrorMessage]);

  const hasMoreMessages = totalPages > 1 && page < totalPages;

  const renderTableBody = () => {
    return messages.map((message, index) => (
      <MessageRow key={`message-${message.id}-${index}`} message={message}/>
    ))
  };

  return (
    <div style={{height: '100%', display: 'flex', flexDirection: 'column'}}>
      <div style={{flexShrink: 0}}>
        <SendingAs/>
      </div>
      <div style={{padding: '1.5rem', flex: 1, overflowY: 'auto'}}>
      <table className='o-table o-table--bordered'>
        <thead>
          <tr>
            <th>Message</th>
            <th>Recipients</th>
            <th>Date/Time</th>
          </tr>
        </thead>
        <tbody>
          {messages.length === 0 && isLoading ? (
            <tr key="loading-0" className='cs-bg--light'>
              <td colSpan="3" className='text-center'>
                <LoadingSpinner />
              </td>
            </tr>
          ) : messages.length === 0 ? (
            <tr className='cs-bg--light'>
              <td colSpan="3" className='text-center'>No messages found</td>
            </tr>
          ) : (
            renderTableBody()
          )}
          {isLoading && messages.length > 0 && (
            <tr key="loading-more" className='cs-bg--light'>
              <td colSpan="3" className='text-center'>
                <LoadingSpinner />
              </td>
            </tr>
          )}
        {hasMoreMessages && (
            <tr className="o-communication__loader" ref={loaderRef} style={{ height: '20px' }}>
              <td colSpan="3"></td>
            </tr>
          )}
        </tbody>
      </table>
      </div>
    </div>
  )
};

GroupMessages.propTypes = {
  fetchGroupMessages: PropTypes.func.isRequired,
  resetErrorMessage: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      body: PropTypes.string.isRequired,
      groups: PropTypes.arrayOf(PropTypes.string),
      cohorts: PropTypes.arrayOf(PropTypes.string),
      sentAt: PropTypes.string.isRequired,
    })
  ).isRequired,
  page: PropTypes.number,
  totalPages: PropTypes.number,
};

GroupMessages.mapStateToProps = state => ({
  isLoading: state.groupMessages.loading,
  messages: state.groupMessages.data || [],
  page: state.groupMessages.page,
  totalPages: state.groupMessages.totalPages,
  hasMore: state.groupMessages.totalPages > 1 && state.groupMessages.page < state.groupMessages.totalPages,
});

export default connect(GroupMessages.mapStateToProps, {
  fetchGroupMessages,
  resetErrorMessage,
})(GroupMessages);
